import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase, generateServiceLink } from "@/integrations/supabase/client";
import { fetchCategories } from "@/utils/categoryUtils";
import { fetchServices } from "@/utils/serviceUtils";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";

export type DashboardViewType = "overview" | "service-requests" | "services" | "categories" | "customers" | "reviews" | "marketing" | "availability" | "settings";

export const useDashboardState = (initialView: DashboardViewType = "overview") => {
  const { t } = useTranslation();
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showServices, setShowServices] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [showCreateService, setShowCreateService] = useState(false);
  const [userCategories, setUserCategories] = useState<any[]>([]);
  const [profile, setProfile] = useState<any | null>(null);
  const [showServiceRequests, setShowServiceRequests] = useState(false);
  const [hasRequests, setHasRequests] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showMarketing, setShowMarketing] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeView, setActiveView] = useState<DashboardViewType>(initialView);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedSettingsSection, setSelectedSettingsSection] = useState("general");
  const [selectedRequestsView, setSelectedRequestsView] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { toast } = useToast();
  const { trackAction } = useAnalytics({ component: "Dashboard", pageView: true });

  const refreshCategories = useCallback(async () => {
    const fetchedCategories = await fetchCategories();
    setCategories(fetchedCategories);
    setUserCategories(fetchedCategories);
  }, []);

  const refreshServices = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      console.log('Starting service refresh in useDashboardState...');
      const updatedServices = await fetchServices();
      console.log('Services fetched successfully in useDashboardState:', updatedServices.length, updatedServices);
      setServices(updatedServices);
    } catch (error) {
      console.error('Error refreshing services in useDashboardState:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh services. Please try again.",
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCategories().then((fetchedCategories) => {
      setCategories(fetchedCategories);
      setUserCategories(fetchedCategories);
    });
    fetchServices().then(setServices);
    fetchProfile();
    checkServiceRequests();
    
    console.log("Dashboard initialView:", initialView);
    console.log("Current path:", location.pathname);
    
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const sectionParam = searchParams.get('section') || params.section;
    
    // Check if we're on the specific request detail page (matches /dashboard/service-requests/:id)
    const isRequestDetail = path.match(/\/dashboard\/service-requests\/[a-zA-Z0-9-]+$/);
    
    if (isRequestDetail) {
      console.log("On request detail page");
      setActiveView("service-requests");
      setShowServiceRequests(true);
      resetAllViews("service-requests");
      setActiveSubmenu("service-requests");
    } else if (initialView === "service-requests" || path === "/dashboard/service-requests") {
      setActiveView("service-requests");
      setShowServiceRequests(true);
      resetAllViews("service-requests");
      setActiveSubmenu("service-requests");
      if (viewParam) {
        setSelectedRequestsView(viewParam);
      }
    } else if (initialView === "services" || path.includes("/services")) {
      setActiveView("services");
      setShowServices(true);
      resetAllViews("services");
      setActiveSubmenu("services");
    } else if (initialView === "categories" || path.includes("/categories")) {
      setActiveView("categories");
      setShowCategories(true);
      resetAllViews("categories");
      setActiveSubmenu("services");
    } else if (initialView === "customers" || path.includes("/customers")) {
      setActiveView("customers");
      setShowCustomers(true);
      resetAllViews("customers");
    } else if (initialView === "reviews" || path.includes("/reviews")) {
      setActiveView("reviews");
      setShowReviews(true);
      resetAllViews("reviews");
    } else if (initialView === "marketing" || path.includes("/marketing")) {
      setActiveView("marketing");
      setShowMarketing(true);
      resetAllViews("marketing");
    } else if (initialView === "availability" || path.includes("/availability")) {
      setActiveView("availability");
      setShowAvailability(true);
      resetAllViews("availability");
    } else if (initialView === "settings" || path.includes("/settings")) {
      setActiveView("settings");
      setShowSettings(true);
      resetAllViews("settings");
      setActiveSubmenu("settings");
      
      if (sectionParam) {
        setSelectedSettingsSection(sectionParam);
      } else {
        setSelectedSettingsSection("general");
      }
    }
  }, [initialView, location.pathname, location.search, params]);

  const resetAllViews = (activeView: string) => {
    if (activeView !== "service-requests") setShowServiceRequests(false);
    if (activeView !== "services") setShowServices(false);
    if (activeView !== "categories") setShowCategories(false);
    if (activeView !== "customers") setShowCustomers(false);
    if (activeView !== "reviews") setShowReviews(false);
    if (activeView !== "marketing") setShowMarketing(false);
    if (activeView !== "availability") setShowAvailability(false);
    if (activeView !== "settings") setShowSettings(false);
  };

  const fetchProfile = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error fetching user in useDashboardState:', userError);
        return;
      }
      
      if (!user) {
        console.log('User not authenticated yet in useDashboardState, will retry on auth state change');
        return;
      }

      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile in useDashboardState:', error);
        return;
      }
        
      console.log('Profile loaded in useDashboardState:', profileData?.id);
      setProfile(profileData);
    } catch (error) {
      console.error('Error in fetchProfile (useDashboardState):', error);
    }
  };

  const fetchServices = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error fetching user for services in useDashboardState:', userError);
        return [];
      }
      
      if (!user) {
        console.log('User not authenticated yet for services in useDashboardState');
        return [];
      }

      console.log('Fetching services for user:', user.id);
      const services = await fetchServicesFromDatabase(user.id);
      return services;
    } catch (error) {
      console.error('Error in fetchServices (useDashboardState):', error);
      return [];
    }
  };

  const fetchServicesFromDatabase = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching services from database:', error);
      return [];
    }
  };

  const checkServiceRequests = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error fetching user for service requests in useDashboardState:', userError);
        return;
      }
      
      if (!user) {
        console.log('User not authenticated yet for service requests in useDashboardState');
        return;
      }

      const { data: requests, error } = await supabase
        .from('service_requests')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (error) {
        console.error('Error checking service requests in useDashboardState:', error);
        return;
      }
      
      setHasRequests(requests && requests.length > 0);
    } catch (error) {
      console.error('Error in checkServiceRequests (useDashboardState):', error);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        console.log('Auth state change detected in useDashboardState:', event);
        // Reload all auth-dependent data
        fetchProfile();
        fetchServices().then(setServices);
        checkServiceRequests();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleViewChange = (view: DashboardViewType) => {
    setActiveView(view);
    trackAction("change_dashboard_tab", { from: activeView, to: view });
    console.log("Changing view to:", view);
    
    if (view === "service-requests") {
      setShowServiceRequests(true);
      resetAllViews("service-requests");
      navigate("/dashboard/service-requests");
    } else if (view === "services") {
      setShowServices(true);
      resetAllViews("services");
      navigate("/dashboard/services");
    } else if (view === "categories") {
      setShowCategories(true);
      resetAllViews("categories");
      navigate("/dashboard/categories");
    } else if (view === "customers") {
      setShowCustomers(true);
      resetAllViews("customers");
      navigate("/dashboard/customers");
    } else if (view === "reviews") {
      setShowReviews(true);
      resetAllViews("reviews");
      navigate("/dashboard/reviews");
    } else if (view === "marketing") {
      setShowMarketing(true);
      resetAllViews("marketing");
      navigate("/dashboard/marketing");
    } else if (view === "availability") {
      setShowAvailability(true);
      resetAllViews("availability");
      navigate("/dashboard/availability");
    } else if (view === "settings") {
      setShowSettings(true); 
      resetAllViews("settings");
      setActiveSubmenu("settings");
    } else {
      resetAllViews("");
      navigate("/dashboard");
    }
  };

  const handleSettingsSectionChange = (section: string) => {
    setSelectedSettingsSection(section);
    navigate(`/dashboard/settings/${section}`);
  };

  const handleViewSite = () => {
    if (profile?.id) {
      const serviceLink = profile.service_page_link || generateServiceLink(profile.id);
      if (serviceLink) {
        const username = serviceLink.includes('/') 
          ? serviceLink.split('/').pop() 
          : serviceLink;
        
        navigate(`/services/${username}`);
      }
    }
  };

 const handleWhatsAppShare = () => {
    if (profile?.id) {
      const serviceLink = profile.service_page_link || generateServiceLink(profile.id);
      if (serviceLink) {
        const username = serviceLink.includes('/')
            ? serviceLink.split('/').pop()
            : serviceLink;

        const serviceUrl = `${window.location.origin}/services/${username}`;
        const message = `${t('marketing.checkOutMyServices')} ${serviceUrl}`;
        window.open(
            `https://wa.me/?text=${encodeURIComponent(message)}`,
            '_blank',
            'noopener,noreferrer'
        );
      }
    }
  };

 const handleTelegramShare = () => {
    if (profile?.id) {
      const serviceLink = profile.service_page_link || generateServiceLink(profile.id);
      if (serviceLink) {
        const username = serviceLink.includes('/')
            ? serviceLink.split('/').pop()
            : serviceLink;

        const serviceUrl = `${window.location.origin}/services/${username}`;
        const message = `${t('marketing.checkOutMyServices')} ${serviceUrl}`;
        window.open(
            `https://t.me/share/url?url=${encodeURIComponent(serviceUrl)}&text=${encodeURIComponent(message)}`,
            '_blank',
            'noopener,noreferrer'
        );
      }
    }
  };

  const toggleSubmenu = (menuName: string) => {
    setActiveSubmenu(activeSubmenu === menuName ? null : menuName);
  };

  return {
    activeSubmenu,
    categories,
    services,
    searchQuery,
    setSearchQuery,
    showServices,
    selectedCategory,
    showCreateService,
    setShowCreateService,
    userCategories,
    profile,
    showServiceRequests,
    hasRequests,
    showCategories,
    showCustomers,
    showMarketing,
    showAvailability,
    showSettings,
    activeView,
    currentUserId,
    selectedSettingsSection,
    selectedRequestsView,
    isRefreshing,
    handleViewChange,
    handleSettingsSectionChange,
    handleViewSite,
    handleWhatsAppShare,
    handleTelegramShare,
    toggleSubmenu,
    resetAllViews,
    refreshServices,
    refreshCategories
  };
};

